import React, { useEffect, useState, useMemo } from "react";
import { Box, Typography, Paper } from "@mui/material";
import { Col, Row } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import GottonReportHeader from "./GottonReportHeader";
import GottonVendorsTable from "./GottonVendorsTable";
import Pagination from "./Pagination";
import CustomersTable from "./CustomersTable";

import {
  fetchCustomersByMonth,
  fetchGottenCustomers,
} from "../../../../redux/slices/ReportsSlice";
import Sidebar from "../../../SideBar";
import { formatMonthForAPI, formatMonthForDisplay } from "../../../../utils/dateUtils";

const CustomersGottenReport = () => {
  const islogin = useSelector((state) => state.user.isLogin);
  const userinfo = useSelector((state) => state.user.userInfo);
  const user = islogin ? JSON.parse(userinfo) : null;
  const {
    gottenCustomers,
    customerMessages,
    loading,
    error,
    messagesLoading,
    messagesError,
  } = useSelector((state) => state.reports);
  const dispatch = useDispatch();

  // State for pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  // State for month filter
  const [selectedMonth, setSelectedMonth] = useState("All");

  // Fetch data
  useEffect(() => {
    dispatch(fetchGottenCustomers());
  }, [dispatch]);

  useEffect(() => {
    if (selectedMonth !== "All") {
      // Convert selectedMonth to API format (2024-May -> 2024-05)
      const apiMonth = formatMonthForAPI(selectedMonth);
      dispatch(fetchCustomersByMonth(apiMonth));
    }
  }, [selectedMonth, dispatch]);

  // Get unique months for dropdown
  const uniqueMonths = useMemo(() => {
    const months = [
      ...new Set(gottenCustomers.map((customer) => customer.MONTH)),
    ]
      .sort((a, b) => b.localeCompare(a)) // Sort in descending order (recent first)
      .map(formatMonthForDisplay); // Convert to display format (2024-May)
    return ["All", ...months];
  }, [gottenCustomers]);

  // Filter and sort customers for "All" view
  const filteredCustomers = useMemo(() => {
    let customers = [...gottenCustomers]; // Create a shallow copy
    if (selectedMonth !== "All") {
      const apiMonth = formatMonthForAPI(selectedMonth); // Convert to API format for filtering
      customers = customers.filter((customer) => customer.MONTH === apiMonth);
    }
    // Sort by MONTH in descending order (recent first)
    return customers.sort((a, b) => b.MONTH.localeCompare(a.MONTH));
  }, [gottenCustomers, selectedMonth]);

  // Calculate paginated data
  const paginatedCustomers = filteredCustomers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  const paginatedMessages = customerMessages.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
    setPage(0);
  };

  return (
    <section className="home-after-loginhydroxychloroquine bg-white">
      <div className="container">
        <Row className="Report-container px-0">
          <Col sm={3} className="stgs_sbar mt-2 mx-0">
            <Sidebar />
          </Col>
          <Col sm={9} className="stgs_content mt-2">
            <Box sx={{ p: 3 }}>
              <GottonReportHeader
                uniqueMonths={uniqueMonths}
                selectedMonth={selectedMonth}
                handleMonthChange={handleMonthChange}
                filteredCustomers={filteredCustomers}
                customerMessages={customerMessages}
                loading={loading}
                messagesLoading={messagesLoading}
              />
              {loading || messagesLoading ? (
                <Typography>Loading...</Typography>
              ) : error || messagesError ? (
                <Typography color="error">
                  Error: {error || messagesError}
                </Typography>
              ) : selectedMonth === "All" ? (
                <>
                  {filteredCustomers.length === 0 ? (
                    <Typography>No data available</Typography>
                  ) : (
                    <>
                      <GottonVendorsTable
                        paginatedCustomers={paginatedCustomers}
                      />
                      <Pagination
                        count={filteredCustomers.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  {customerMessages.length === 0 ? (
                    <Typography>
                      No messages available for this month
                    </Typography>
                  ) : (
                    <>
                      <CustomersTable paginatedMessages={paginatedMessages} />
                      <Pagination
                        count={customerMessages.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        handleChangePage={handleChangePage}
                        handleChangeRowsPerPage={handleChangeRowsPerPage}
                      />
                    </>
                  )}
                </>
              )}
            </Box>
          </Col>
        </Row>
      </div>
    </section>
  );
};

export default CustomersGottenReport;
